// In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.
// NexoraX NexoraX QR Dispatcher - "Tactical Revenue Grid" Aesthetics
// Feature: Bulk Generation of SecurePayment-Linked QR Codes for 10,000 Nodes
// Intellectual Property Status: Patent-Readiness Protected Assets, © NexoraX Digital Ltd (SC887095)

import React, { useState, useEffect, useRef } from 'react';
import { 
  QrCode, Sparkles, Server, CheckCircle2, ShieldAlert, Cpu, 
  Activity, ArrowUpRight, Copy, Check, Download, Layers, Play, 
  Pause, RefreshCw, Send, Trash2, Landmark, Sliders, Monitor, 
  Tv, Building, ExternalLink, HelpCircle, FileX, Database, BookOpen,
  Bell, Volume2, Terminal
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

// Define Interface for generated nodes
interface DispatchedNode {
  nodeId: string;
  SecurePaymentLink: string;
  branding: string;
  status: 'PENDING' | 'GENERATED' | 'DISPATCHED';
  timestamp: string;
}

export const NexoraXQrDispatcher: React.FC = () => {
  // Config States
  const [SecurePaymentBaseLink, setSecurePaymentBaseLink] = useState<string>("https://pay.SecurePayment.co/pay/9fd1aa7e-20b1-44b0-bf57-b75944dd5134");
  const [nodePrefix, setNodePrefix] = useState<string>("CAT-");
  const [nodeVolume, setNodeVolume] = useState<number>(10000);
  const [brandingMode, setBrandingMode] = useState<'NEXORAX-NEXORAX' | 'DIRECTOR-MANDATE' | 'VANGUARD-GRID'>('NEXORAX-NEXORAX');
  const [canvasPreset, setCanvasPreset] = useState<'tv_billboard' | 'instore_pos' | 'ledger_audit'>('tv_billboard');
  const [selectedNodeId, setSelectedNodeId] = useState<string>("CAT-19");
  
  // Custom Visual Themes for QR codes
  const [qrColorTheme, setQrColorTheme] = useState<'amber' | 'cyan' | 'emerald' | 'rose'>('amber');
  const [qrPixelStyle, setQrPixelStyle] = useState<'pixelated' | 'rounded' | 'cyberpunk'>('cyberpunk');
  const [includeCenterLogo, setIncludeCenterLogo] = useState<boolean>(true);

  // Simulation States
  const [isDispatching, setIsDispatching] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [logs, setLogs] = useState<string[]>([
    "NEXORAX DEFENSIVE NODE-07 ONLINE // STANDBY QUEUE EMPTY",
    "VERIFIED PREAMBLE: ARCHITECT SANCTIONED CHANNELS SECURED",
    "READY FOR BULK DISPATCH CONCURRENT MATRIX DEPLOYMENT (MAX 10,000)..."
  ]);

  // NexoraX QR Asset Hub States
  const [selectedHubNode, setSelectedHubNode] = useState<string>("Node-CAT-19");
  const [hubDispatchStatus, setHubDispatchStatus] = useState<'IDLE' | 'DISPATCHING' | 'DONE'>('IDLE');
  const [hubDispatchProgress, setHubDispatchProgress] = useState<number>(0);
  const [hubLogs, setHubLogs] = useState<string[]>([
    "CENTRALIZED ASSET STORAGE ONLINE",
    "PENDING USER INSTRUCTIONS FOR BULK GEOGRAPHIC HANDSHAKES..."
  ]);
  const [hubVerifiedToken, setHubVerifiedToken] = useState<string>("TX-SECURE-99A8F53B");
  const [generatedNodes, setGeneratedNodes] = useState<DispatchedNode[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(10);
  
  // Copy state
  const [copiedText, setCopiedText] = useState<boolean>(false);
  
  // Stats accumulation based on 1% Levy and 20% Tax logic
  const [simulatedGrossHarvest, setSimulatedGrossHarvest] = useState<number>(540000);
  const NexoraXLevy = simulatedGrossHarvest * 0.01;
  const lifetimeTax = (simulatedGrossHarvest - NexoraXLevy) * 0.20;
  const netSettlement = simulatedGrossHarvest - NexoraXLevy - lifetimeTax;

  // Neural Push Engine States & Simulated Telemetry
  const [isPushSimulationActive, setIsPushSimulationActive] = useState<boolean>(false);
  const [pushLogs, setPushLogs] = useState<Array<{ id: string; time: string; msg: string; type: 'ALERT' | 'INFO' | 'SYNC' }>>([
    { id: '1', time: new Date().toLocaleTimeString(), msg: "Neural Push Engine status initialized: SLA 0.8s STANDBY", type: 'INFO' },
    { id: '2', time: new Date().toLocaleTimeString(), msg: "Monitoring active on all 10,000 QR dispatch nodes", type: 'SYNC' }
  ]);
  const [activeUiToasts, setActiveUiToasts] = useState<Array<{ id: string; nodeID: string; amount: number; time: string }>>([]);
  const pushIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // High-Conversion Event Trigger & Local Storage/SecurePayment Logger Sync
  const triggerPushNotification = (nodeID: string, settlementAmount: number) => {
    const timestamp = new Date().toLocaleTimeString();
    const eventMsg = `[ALERT] High-Conversion Event on Node: ${nodeID} | Value: £${settlementAmount.toLocaleString()}`;
    console.log(eventMsg);

    // Queue log for 10:00 AM Revenue Reconciliation
    setPushLogs(prev => [
      { id: Date.now().toString(), time: timestamp, msg: `Node ${nodeID} settled £${settlementAmount.toLocaleString()}`, type: 'ALERT' },
      ...prev.slice(0, 49)
    ]);

    // Push internal UI Floating Toast
    const newToastId = Date.now().toString() + Math.random().toString();
    setActiveUiToasts(prev => [
      { id: newToastId, nodeID, amount: settlementAmount, time: timestamp },
      ...prev
    ]);
    setTimeout(() => {
      setActiveUiToasts(prev => prev.filter(t => t.id !== newToastId));
    }, 4500);

    // Update cumulative Gross harvest under Lead Director's 1% Levy logic
    setSimulatedGrossHarvest(prev => prev + settlementAmount);

    // Browser Notification Core Permission Hook & Dispatch
    if (typeof window !== 'undefined' && 'Notification' in window) {
      if (Notification.permission === 'granted') {
        new Notification("NexoraX Conversion Alert", {
          body: `Node ${nodeID} has just settled £${settlementAmount.toLocaleString()}!`,
          icon: "/nexorax-logo-gold.png"
        });
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission().then((permission) => {
          if (permission === 'granted') {
            new Notification("NexoraX Conversion Alert", {
              body: `Node ${nodeID} has just settled £${settlementAmount.toLocaleString()}!`,
              icon: "/nexorax-logo-gold.png"
            });
          }
        });
      }
    }
  };

  const logsEndRef = useRef<HTMLDivElement>(null);
  const simulationIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Generate random QR Matrix pixels based on inputs
  const [qrMatrix, setQrMatrix] = useState<boolean[][]>([]);

  useEffect(() => {
    if (isPushSimulationActive) {
      pushIntervalRef.current = setInterval(() => {
        const randomNodeNum = Math.floor(Math.random() * 10000) + 1;
        const randomAmount = Math.floor(Math.random() * 450) + 50; 
        triggerPushNotification(`CAT-${randomNodeNum}`, randomAmount);
      }, 3500);
    } else {
      if (pushIntervalRef.current) {
        clearInterval(pushIntervalRef.current);
        pushIntervalRef.current = null;
      }
    }
    return () => {
      if (pushIntervalRef.current) {
        clearInterval(pushIntervalRef.current);
      }
    };
  }, [isPushSimulationActive]);

  useEffect(() => {
    generateInteractiveQrMatrix();
  }, [selectedNodeId, qrPixelStyle, qrColorTheme, SecurePaymentBaseLink]);

  const generateInteractiveQrMatrix = () => {
    // Generate a beautiful 21x21 QR module matrix
    const size = 21;
    const matrix: boolean[][] = Array(size).fill(null).map(() => Array(size).fill(false));

    // Construct standard QR corners (finder patterns) (7x7 modules)
    const setFinderPattern = (rowStart: number, colStart: number) => {
      for (let r = 0; r < 7; r++) {
        for (let c = 0; c < 7; c++) {
          const isBorder = r === 0 || r === 6 || c === 0 || c === 6;
          const isCenter = r >= 2 && r <= 4 && c >= 2 && c <= 4;
          matrix[rowStart + r][colStart + c] = isBorder || isCenter;
        }
      }
    };

    // Set 3 finder patterns typical of QR standards
    setFinderPattern(0, 0); // Top-Left
    setFinderPattern(0, size - 7); // Top-Right
    setFinderPattern(size - 7, 0); // Bottom-Left

    // Draw timing lines
    for (let i = 8; i < size - 8; i++) {
      matrix[6][i] = i % 2 === 0;
      matrix[i][6] = i % 2 === 0;
    }

    // Set center logo safety zone if checkbox enabled
    const logoRadius = includeCenterLogo ? 3 : 0;
    const center = Math.floor(size / 2);

    // Randomize functional data columns with mathematical pseudo-hashes based on inputs
    const keyString = `${selectedNodeId}-${SecurePaymentBaseLink}-${qrPixelStyle}-${qrColorTheme}`;
    let hashIndex = 0;
    const getPseudoHashValue = () => {
      const charCode = keyString.charCodeAt(hashIndex % keyString.length) || 42;
      hashIndex++;
      return (charCode * hashIndex) % 7 === 0;
    };

    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        // Skip finder pattern zones, center logo zone, and timing tracks
        const isInFinderTopLeft = r < 8 && c < 8;
        const isInFinderTopRight = r < 8 && c >= size - 8;
        const isInFinderBottomLeft = r >= size - 8 && c < 8;
        const isInCenterLogo = includeCenterLogo && 
          r >= center - logoRadius && r <= center + logoRadius && 
          c >= center - logoRadius && c <= center + logoRadius;
        
        const isTimingLine = r === 6 || c === 6;

        if (!isInFinderTopLeft && !isInFinderTopRight && !isInFinderBottomLeft && !isInCenterLogo && !isTimingLine) {
          matrix[r][c] = getPseudoHashValue();
        }
      }
    }

    setQrMatrix(matrix);
  };

  // Safe scroll simulation logs
  useEffect(() => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);

  // Bulk Dispatch Simulator
  const launchBulkDispatch = () => {
    if (isDispatching) return;
    setIsDispatching(true);
    setProgress(0);
    
    // Clear previously generated nodes table preview to run fresh high-fidelity rows
    setGeneratedNodes([]);
    
    const nodeCount = Math.min(nodeVolume, 100); // Generate 100 visual node rows while scaling stats to target volume
    const newLogs = [
      `[MANDATE] INITIALIZING NEXORAX BULK GENERATION AT TENTATIVE METRICS FOR ${nodeVolume} NODES...`,
      `[SECURITY] VERIFYING SecurePayment ROUTING MATRIX TO ACCOUNT SORT CODE Verified Payment Configuration...`,
      `[LEDGER] ESTABLISHING PARALLEL WRITE PIPELINES TO ARCHIVAL NexoraX Resource Library...`,
    ];
    setLogs(newLogs);

    let step = 0;
    simulationIntervalRef.current = setInterval(() => {
      step++;
      const currentProgress = Math.floor((step / nodeCount) * 100);
      setProgress(currentProgress);
      
      const nodeId = `${nodePrefix}${1000 + step}`;
      const simulatedRate = SecurePaymentBaseLink.includes("?") 
        ? `${SecurePaymentBaseLink}&nodeID=${nodeId}` 
        : `${SecurePaymentBaseLink}?nodeID=${nodeId}`;

      const newNode: DispatchedNode = {
        nodeId,
        SecurePaymentLink: simulatedRate,
        branding: brandingMode,
        status: step % 3 === 0 ? 'DISPATCHED' : 'GENERATED',
        timestamp: new Date().toLocaleTimeString()
      };

      setGeneratedNodes(prev => [newNode, ...prev]);

      // Cycle logging messages corresponding to 0.8s benchmark velocities
      if (step % 4 === 0) {
        setLogs(prev => [
          ...prev,
          `[DISPATCH] Generated QR for ${nodeId} (${brandingMode}). Ready for TV/Billboard deployment.`,
          `[VAULT] Node-${nodeId} metadata mirrored to gdrive:Nexorax_Ecosystem_Backups/ via CloudSync-02.`
        ]);
      } else if (step % 7 === 1) {
        setLogs(prev => [
          ...prev,
          `[SecurePayment] Direct open banking registration handshake verified for ${nodeId}.`
        ]);
      } else {
        setLogs(prev => [
          ...prev,
          `[LEVY] 1% Perpetual 3% Community Allocation compiled for ${nodeId} // Net Settlement allocated.`
        ]);
      }

      // Live mock harvest increment
      setSimulatedGrossHarvest(prev => prev + Math.floor(Math.random() * 1500 + 800));

      if (step >= nodeCount) {
        if (simulationIntervalRef.current) clearInterval(simulationIntervalRef.current);
        setIsDispatching(false);
        setProgress(100);
        setLogs(prev => [
          ...prev,
          `\n[SUCCESS] SECURED DISPATCH COMPLETE // SYSTEM STATE LOUDER AT 100% SUCCESS RATIO`,
          `[METRICS] Total Gross Harvest: £2,820,000 | 3% Community Allocation: £28,200 | Net Settlement: £2,227,800`,
          `[REPORT] 10,000 Nodes dispatch logs synced to Google Drive NexoraX Resource Library successfully.`
        ]);
      }
    }, 150);
  };

  const cancelDispatch = () => {
    if (simulationIntervalRef.current) {
      clearInterval(simulationIntervalRef.current);
    }
    setIsDispatching(false);
    setLogs(prev => [...prev, "[HALTED] DISPATCH INTERRUPTED BY CHIEF EXECUTIVE ORDER."]);
  };

  const handleCopyLink = () => {
    const fullExp = `// NexoraX NexoraX QR Dispatcher\nconst url = "${SecurePaymentBaseLink}";\nconst nodeId = "${selectedNodeId}";\n// Target: SecurePayment Checkouts\nroute(nodeId, url);`;
    navigator.clipboard.writeText(fullExp);
    setCopiedText(true);
    setTimeout(() => setCopiedText(false), 2000);
  };

  // Dynamic colors for QR matrix style based on selectors
  const getThemeHighlightColor = () => {
    switch(qrColorTheme) {
      case 'cyan': return 'text-cyan-400 bg-cyan-950/40 border-cyan-500/40';
      case 'emerald': return 'text-emerald-400 bg-emerald-950/40 border-emerald-500/40';
      case 'rose': return 'text-rose-400 bg-rose-950/40 border-rose-500/40';
      default: return 'text-amber-500 bg-amber-950/40 border-amber-500/40';
    }
  };

  const getQRFillClass = (isActiveCell: boolean) => {
    if (isActiveCell) {
      switch(qrColorTheme) {
        case 'cyan': return 'bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.6)]';
        case 'emerald': return 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.6)]';
        case 'rose': return 'bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.6)]';
        default: return 'bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.6)]';
      }
    }
    return 'bg-neutral-900/60';
  };

  const getPresetAccentClass = () => {
    switch (canvasPreset) {
      case 'tv_billboard':
        return 'border-double border-8 border-red-500 bg-[#02050e] ring-4 ring-offset-4 ring-offset-black ring-red-500/50 shadow-[0_0_80px_rgba(239,68,68,0.3)]';
      case 'instore_pos':
        return 'border border-cyan-500/60 bg-black/90 shadow-[0_0_40px_rgba(6,182,212,0.15)]';
      case 'ledger_audit':
        return 'border-2 border-dashed border-emerald-500/40 bg-zinc-950/95';
    }
  };

  // Pagination rows
  const indexOfLastNode = currentPage * itemsPerPage;
  const indexOfFirstNode = indexOfLastNode - itemsPerPage;
  const currentNodes = generatedNodes.slice(indexOfFirstNode, indexOfLastNode);
  const totalPages = Math.ceil(generatedNodes.length / itemsPerPage);

  return (
    <div className="w-full max-w-7xl mx-auto px-4 py-8 space-y-10 min-h-screen text-slate-100">
      
      {/* Arabic/English praise preamble sequence */}
      <div className="text-center space-y-1 border-b border-amber-500/20 pb-5 max-w-xl mx-auto">
        <p className="text-[11px] font-extrabold font-mono tracking-[0.2em] text-amber-500 uppercase select-all animate-pulse">
          In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.
        </p>
        <p className="text-[9px] font-mono tracking-[0.3em] text-[#00ffcc] uppercase opacity-75">
          Authorized Neural Core Alignment // NexoraX communications Node
        </p>
      </div>

      {/* Main Header Widget */}
      <div className="relative p-6 md:p-10 bg-[#02050f]/90 border border-amber-500/50 overflow-hidden rounded-3xl backdrop-blur-xl shadow-[0_0_50px_rgba(245,158,11,0.15)]">
        {/* Background circuit grid */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="h-full w-full bg-[linear-gradient(to_right,#1f2937_1px,transparent_1px),linear-gradient(to_bottom,#1f2937_1px,transparent_1px)] bg-[size:32px_32px]" />
        </div>

        <div className="relative z-10 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-6">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-amber-500 animate-[ping_2s_infinite]"></span>
              <span className="text-[10px] font-mono tracking-[0.4em] font-extrabold text-amber-400 uppercase">
                NODE-07 // QUANTUM DEPLOYMENT GRID
              </span>
            </div>
            <h1 className="text-slate-100 font-extrabold text-3xl md:text-4xl uppercase tracking-wider leading-none">
              NexoraX QR Dispatcher
            </h1>
            <p className="text-neutral-400 font-sans text-xs max-w-2xl leading-relaxed">
              Automated high-capacity deployment matrix mapping Node IDs directly to Secure Payment Provider Checkout APIs. Generates verified print-ready vector-quality visual targets secure for television broadcast and massive billboard scaling under Lead Director's 1% 3% Community Allocation protocol.
            </p>
          </div>

          <div className="flex items-center gap-3 bg-neutral-950 p-4 border border-zinc-800 rounded-2xl shrink-0 self-start md:self-auto">
            <Cpu className="w-8 h-8 text-amber-400 animate-pulse shrink-0" />
            <div className="font-mono text-[10px]">
              <div className="text-neutral-500 uppercase tracking-widest text-[9px] font-black">ACTIVE ROUTINE LINKED</div>
              <div className="text-emerald-400 font-extrabold font-sans text-xs mt-0.5">Sort Code: Verified Payment Configuration</div>
              <div className="text-emerald-400 font-extrabold font-sans text-xs">Account: Secure Billing Provider</div>
            </div>
          </div>
        </div>
      </div>

      {/* Live Accounting Summary Metrics Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { 
            label: "Virtual Gross Harvest", 
            value: `£${simulatedGrossHarvest.toLocaleString()}`, 
            desc: "Running simulated ledger sum", 
            color: "text-amber-500" 
          },
          { 
            label: "1% 3% Community Allocation (Dunfermline)", 
            value: `£${NexoraXLevy.toLocaleString(undefined, {maximumFractionDigits:2})}`, 
            desc: "Allocating 33.33% evenly to Charities 1, 2, 3", 
            color: "text-cyan-400" 
          },
          { 
            label: "10% Maintenance & Hosting Fee (10% Maintenance & Hosting Fee)", 
            value: `£${lifetimeTax.toLocaleString(undefined, {maximumFractionDigits:2})}`, 
            desc: "Office of Director extracted protocol", 
            color: "text-rose-500" 
          },
          { 
            label: "Net Balance Routed to SecurePayment", 
            value: `£${netSettlement.toLocaleString(undefined, {maximumFractionDigits:2})}`, 
            desc: "Settled safely on SecurePayment corporate node", 
            color: "text-emerald-400" 
          }
        ].map((stat, i) => (
          <div key={i} className="p-5 rounded-2xl bg-neutral-950 border border-neutral-850 hover:border-amber-500/20 transition-all duration-300 flex flex-col justify-between">
            <span className="text-[9px] font-mono tracking-widest text-neutral-500 uppercase font-bold">{stat.label}</span>
            <div className="my-3 font-mono">
              <span className={`text-xl md:text-2xl font-black ${stat.color}`}>{stat.value}</span>
            </div>
            <span className="text-[10px] text-zinc-500 font-sans italic">{stat.desc}</span>
          </div>
        ))}
      </div>

      {/* Primary Content Split - Controls Left, Visual Showcase Right */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch font-mono">
        
        {/* Left Side: Parameters Customizer Panel (Col 7) */}
        <div className="lg:col-span-7 flex flex-col justify-between gap-6 bg-[#040815]/95 p-6 rounded-3xl border border-neutral-850">
          
          <div className="space-y-5">
            <div className="flex items-center gap-2 border-b border-neutral-900 pb-3">
              <Sliders className="w-5 h-5 text-amber-500 animate-pulse" />
              <h2 className="text-sm font-extrabold uppercase tracking-widest text-white">
                Deployment Matrix Configurator
              </h2>
            </div>

            {/* SecurePayment link input */}
            <div className="space-y-2">
              <label className="text-[11px] font-bold text-neutral-400 uppercase flex items-center justify-between">
                <span>1. SecurePayment Merchant API Link Pattern:</span>
                <span className="text-[8px] text-zinc-500">SORT Verified Payment Configuration // ACC Secure Billing Provider</span>
              </label>
              <input 
                type="text" 
                value={SecurePaymentBaseLink} 
                onChange={(e) => setSecurePaymentBaseLink(e.target.value)} 
                className="w-full bg-black border border-neutral-850 rounded-xl px-4 py-3 text-xs text-white focus:outline-none focus:border-amber-500"
                placeholder="https://pay.SecurePayment.co/pay/9fd1aa7e-..."
              />
            </div>

            {/* Prefix & Volumes grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-[11px] font-bold text-neutral-400 uppercase">
                  2. Node Categorization Prefix:
                </label>
                <input 
                  type="text" 
                  value={nodePrefix} 
                  onChange={(e) => setNodePrefix(e.target.value)} 
                  className="w-full bg-black border border-neutral-850 rounded-xl px-4 py-3 text-xs text-white focus:outline-none focus:border-amber-500"
                  placeholder="e.g., CAT- or VANGUARD-"
                />
              </div>

              <div className="space-y-2">
                <label className="text-[11px] font-bold text-neutral-400 uppercase flex items-center justify-between">
                  <span>3. Nodes Volume:</span>
                  <span className="text-amber-500 font-extrabold">{nodeVolume.toLocaleString()} Nodes</span>
                </label>
                <select 
                  value={nodeVolume} 
                  onChange={(e) => setNodeVolume(Number(e.target.value))} 
                  className="w-full bg-black border border-neutral-850 rounded-xl px-4 py-3 text-xs text-white focus:outline-none focus:border-amber-500 cursor-pointer"
                >
                  <option value={10}>10 Individual Targeted Nodes</option>
                  <option value={100}>100 Medium Scale Nodes</option>
                  <option value={1000}>1,000 High-Volume Cluster Nodes</option>
                  <option value={10000}>10,000 Complete NexoraX Network Target Nodes</option>
                </select>
              </div>
            </div>

            {/* Customizer Sub-section */}
            <div className="p-4 rounded-2xl bg-black/40 border border-neutral-900 space-y-4">
              <label className="text-[10px] text-zinc-400 font-extrabold tracking-wider uppercase block border-b border-neutral-900 pb-2">
                Visual Aesthetic Calibration
              </label>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                
                {/* QR Module Theme */}
                <div className="space-y-2">
                  <span className="text-[10px] text-neutral-500 block">QR Color Schema:</span>
                  <div className="flex gap-2.5">
                    {[
                      { id: 'amber', class: 'bg-amber-500 border-amber-400 text-amber-500' },
                      { id: 'cyan', class: 'bg-cyan-500 border-cyan-400 text-cyan-500' },
                      { id: 'emerald', class: 'bg-emerald-500 border-emerald-400 text-emerald-500' },
                      { id: 'rose', class: 'bg-rose-500 border-rose-400 text-rose-500' }
                    ].map(col => (
                      <button 
                        key={col.id}
                        type="button"
                        onClick={() => setQrColorTheme(col.id as any)}
                        className={`w-5 h-5 rounded-full border-2 cursor-pointer transition-all ${col.class} ${
                          qrColorTheme === col.id ? 'ring-2 ring-white scale-125' : 'opacity-60 hover:opacity-100'
                        }`}
                        title={col.id}
                      />
                    ))}
                  </div>
                </div>

                {/* Pixel styling structure */}
                <div className="space-y-2">
                  <span className="text-[10px] text-neutral-500 block">Pixel Module Type:</span>
                  <div className="flex bg-neutral-950 p-1 border border-zinc-850 rounded-lg">
                    {['sharp', 'cyber'].map(style => (
                      <button 
                        key={style}
                        type="button"
                        onClick={() => setQrPixelStyle(style === 'sharp' ? 'pixelated' : 'cyberpunk')}
                        className={`flex-1 text-[9px] uppercase font-bold py-1 px-2 rounded cursor-pointer transition-all ${
                          (style === 'sharp' && qrPixelStyle === 'pixelated') || (style === 'cyber' && qrPixelStyle === 'cyberpunk')
                            ? 'bg-neutral-850 text-white' 
                            : 'text-neutral-500 hover:text-neutral-300'
                        }`}
                      >
                        {style}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Branding Core Label Block */}
                <div className="space-y-2">
                  <span className="text-[10px] text-neutral-500 block">Active Branding Badge:</span>
                  <label className="flex items-center gap-2 cursor-pointer text-[10px] text-neutral-400">
                    <input 
                      type="checkbox" 
                      className="rounded border-zinc-800 bg-[#030612] text-amber-500 focus:ring-amber-500 cursor-pointer"
                      checked={includeCenterLogo}
                      onChange={(e) => setIncludeCenterLogo(e.target.checked)}
                    />
                    <span>Central logo embed</span>
                  </label>
                </div>

              </div>

              {/* Sub-selector for Branding text */}
              <div className="space-y-2 pt-2 border-t border-neutral-900">
                <span className="text-[10px] text-neutral-500 block">Node Branding Inscription:</span>
                <div className="flex gap-2">
                  {[
                    { id: 'NEXORAX-NEXORAX', label: "Nexorax NexoraX" },
                    { id: 'DIRECTOR-MANDATE', label: "Director Mandate" },
                    { id: 'VANGUARD-GRID', label: "Vanguard Grid Node" }
                  ].map(spec => (
                    <button
                      key={spec.id}
                      type="button"
                      onClick={() => setBrandingMode(spec.id as any)}
                      className={`flex-1 py-1 px-2 border rounded text-[9px] transition-all cursor-pointer ${
                        brandingMode === spec.id 
                          ? 'bg-amber-500/15 border-amber-500/50 text-amber-400 font-bold' 
                          : 'border-zinc-850 hover:border-zinc-700 text-zinc-400 bg-transparent'
                      }`}
                    >
                      {spec.label}
                    </button>
                  ))}
                </div>
              </div>

            </div>

            {/* Launch Action triggers */}
            <div className="pt-4 border-t border-neutral-900 flex flex-col sm:flex-row items-stretch gap-4">
              {!isDispatching ? (
                <button
                  onClick={launchBulkDispatch}
                  className="flex-1 py-3.5 rounded-xl bg-amber-500 hover:bg-amber-450 hover:scale-[1.01] text-black font-black uppercase text-xs tracking-widest flex items-center justify-center gap-2 transition-all shadow-[0_0_30px_rgba(245,158,11,0.25)] cursor-pointer"
                >
                  <Play className="w-4 h-4 text-black" />
                  Initialize Bulk Dispatch ({nodeVolume.toLocaleString()} Nodes)
                </button>
              ) : (
                <button
                  onClick={cancelDispatch}
                  className="flex-1 py-3.5 rounded-xl bg-red-650 hover:bg-red-600 hover:scale-[1.01] text-white font-black uppercase text-xs tracking-widest flex items-center justify-center gap-2 transition-all shadow-[0_0_30px_rgba(220,38,38,0.25)] cursor-pointer"
                >
                  <Pause className="w-4 h-4 text-white" />
                  HALT QUEUE TRANSMISSION
                </button>
              )}

              <button
                type="button"
                onClick={() => {
                  setSimulatedGrossHarvest(540000);
                  setGeneratedNodes([]);
                  setLogs([
                    "NEXORAX DEFENSIVE NODE-07 ONLINE // STANDBY QUEUE EMPTY",
                    "COMMENCING DESTRUCTIVE QUEUE SCRUB..."
                  ]);
                }}
                className="px-5 py-3.5 border border-zinc-850 hover:border-zinc-700 bg-neutral-950/70 hover:bg-neutral-950 text-neutral-400 hover:text-white rounded-xl text-xs uppercase flex items-center justify-center gap-2 cursor-pointer transition-colors"
                title="Reset simulation parameters"
                disabled={isDispatching}
              >
                <Trash2 className="w-4 h-4" />
                Reset
              </button>
            </div>

          </div>

          {/* Active Generation Telemetry Box */}
          <div className="space-y-2 mt-6">
            <div className="flex items-center justify-between text-[11px] font-bold uppercase text-neutral-400">
              <span>Concurrent Process Queue Feed:</span>
              <span className="text-[10px] text-[#00ffcc] font-extrabold">{progress}% Secure Link</span>
            </div>

            {/* Progress segment bar */}
            <div className="w-full bg-neutral-950 h-2.5 rounded-full overflow-hidden border border-neutral-900">
              <div 
                className="bg-gradient-to-r from-amber-500 to-[#00ffcc] h-full transition-all duration-150 rounded-full"
                style={{ width: `${progress}%` }}
              />
            </div>

            {/* Console Log readout */}
            <div className="bg-black text-neutral-400 p-4 border border-neutral-900 rounded-2xl h-44 overflow-y-auto font-mono text-[9px] space-y-1.5 flex flex-col shadow-inner">
              {logs.map((log, index) => (
                <div 
                  key={index} 
                  className={`leading-relaxed break-words ${
                    log.includes("SUCCESS") || log.includes("Levy Compiled") || log.includes("[LEVY]")
                      ? 'text-emerald-400' 
                      : log.includes("HALTED") || log.includes("[HALT]")
                        ? 'text-red-400 animate-pulse font-bold' 
                        : log.includes("Generated QR") || log.includes("[DISPATCH]")
                          ? 'text-slate-200'
                          : 'text-[#00ffff]'
                  }`}
                >
                  &gt; {log}
                </div>
              ))}
              <div ref={logsEndRef} />
            </div>
          </div>

        </div>

        {/* Right Side: Visual Showcase Branded Artwork Frame (Col 5) */}
        <div className="lg:col-span-5 flex flex-col justify-between gap-6">

          {/* Interactive Preset Card */}
          <div className="p-6 bg-neutral-950 rounded-3xl border border-neutral-850 flex flex-col gap-4">
            <div className="flex items-center justify-between border-b border-neutral-900 pb-2.5">
              <span className="text-[10px] font-extrabold tracking-widest text-neutral-500 uppercase">
                Vector Canvas Canvas Preset
              </span>
              <div className="flex bg-neutral-900 p-0.5 border border-zinc-850 rounded-lg shrink-0">
                {[
                  { id: 'tv_billboard', icon: Tv, label: 'Broadcast' },
                  { id: 'instore_pos', icon: Monitor, label: 'In-Store' },
                  { id: 'ledger_audit', icon: Database, label: 'Invoice' }
                ].map((item) => {
                  const IconComp = item.icon;
                  return (
                    <button 
                      key={item.id}
                      type="button"
                      onClick={() => setCanvasPreset(item.id as any)}
                      className={`py-1.5 px-2.5 rounded text-[9px] uppercase font-bold flex items-center gap-1 transition-all cursor-pointer ${
                        canvasPreset === item.id 
                          ? 'bg-neutral-800 text-amber-500 shadow-inner' 
                          : 'text-zinc-500 hover:text-zinc-300'
                      }`}
                      title={item.label}
                    >
                      <IconComp className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ARTWORK FRAME: QR Code Core representation */}
            <div className="flex flex-col items-center justify-center py-6">
              
              <div className={`p-6 rounded-2xl transition-all duration-500 relative ${getPresetAccentClass()}`}>
                
                {/* Visual TV elements if broadcast preview selected */}
                {canvasPreset === 'tv_billboard' && (
                  <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-red-600 text-white font-sans text-[7px] font-black tracking-widest px-2 py-0.5 rounded uppercase animate-pulse z-20 flex items-center gap-1 border border-red-400">
                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-ping"></span>
                    LIVE TV broadcast ready
                  </div>
                )}

                {/* Branded Central Badge overlay text context on bounds */}
                <div className="relative p-3 bg-neutral-950 border-2 border-zinc-850 rounded-xl">
                  
                  {/* Dynamic Custom QR Drawing Grid (21x21 modules styled custom) */}
                  <div className="grid grid-cols-21 gap-[1.5px] p-2 bg-neutral-950 select-none w-64 h-64 relative">
                    
                    {qrMatrix.map((rowArr, rowIndex) => 
                      rowArr.map((isCellActive, colIndex) => {
                        const styleClass = getQRFillClass(isCellActive);
                        return (
                          <div 
                            key={`${rowIndex}-${colIndex}`} 
                            className={`w-full aspect-square transition-colors duration-300 ${styleClass} ${
                              qrPixelStyle === 'cyberpunk' && isCellActive ? 'rounded-[2px]' : ''
                            }`}
                          />
                        );
                      })
                    )}

                    {/* Highly polished Central branding badge overlay if checked */}
                    {includeCenterLogo && (
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-neutral-950 p-[5px] rounded-lg border-2 border-neutral-850 shadow-2xl flex items-center justify-center z-10 w-12 h-12">
                        <div className="w-full h-full bg-black flex flex-col justify-center items-center text-[7px] font-mono leading-none border border-neutral-800 rounded">
                          <span className="text-amber-500 font-black tracking-tighter">NEXO</span>
                          <span className="text-[#00ffcc] font-extrabold text-[5px] mt-0.5 tracking-tighter">RAX</span>
                        </div>
                      </div>
                    )}

                  </div>

                </div>

                {/* QR Metadata Footer Label Bar */}
                <div className="mt-3.5 border-t border-neutral-900/40 pt-2 flex flex-col items-center gap-1 text-[8px] font-mono text-center">
                  <span className="text-white font-extrabold uppercase tracking-widest select-all">
                    {brandingMode === 'NEXORAX-NEXORAX' ? 'NEXORAX-NEXORAX' : brandingMode === 'DIRECTOR-MANDATE' ? 'DIRECTOR Services Guide' : 'VANGUARD-GRID'}
                  </span>
                  <span className="text-zinc-500 tracking-wider">
                    {selectedNodeId} // LEVY RECOGNITION CONDUIT
                  </span>
                </div>

              </div>
              
            </div>

            {/* Quick Interactive Selector table to select the Active Node preview */}
            <div className="space-y-2">
              <span className="text-[10px] text-zinc-500 font-bold uppercase block">
                Preview Interactive Node Details:
              </span>
              <div className="grid grid-cols-5 gap-1.5">
                {["CAT-15", "CAT-18", "CAT-19", "CAT-44", "CAT-99"].map((node) => (
                  <button
                    key={node}
                    onClick={() => {
                      setSelectedNodeId(node);
                      setLogs(prev => [...prev, `[PREVIEW] Interactive preview canvas synchronized to node: ${node}`]);
                    }}
                    className={`py-1 rounded text-[10px] font-mono border transition-all cursor-pointer ${
                      selectedNodeId === node 
                        ? 'bg-amber-500/15 border-amber-500/80 text-amber-500 font-extrabold' 
                        : 'bg-[#040913] border-neutral-900 text-slate-500 hover:border-neutral-700'
                    }`}
                  >
                    {node}
                  </button>
                ))}
              </div>
            </div>

            {/* Detailed metadata link view panel */}
            <div className="p-4 rounded-xl bg-black border border-neutral-900 space-y-2">
              <div className="flex items-center justify-between text-[10px] font-bold text-neutral-450 uppercase">
                <span>SecurePayment Link For {selectedNodeId}:</span>
                <span className="text-emerald-400 font-extrabold">0.8s Handshake</span>
              </div>
              
              <div className="bg-[#030610] p-3 rounded-lg border border-neutral-850 flex items-center justify-between gap-3 font-mono text-[9px] text-[#00ffcc] break-all select-all">
                <span>{SecurePaymentBaseLink}?nodeID={selectedNodeId}</span>
                <button 
                  type="button"
                  onClick={handleCopyLink}
                  className="p-1 px-2 rounded bg-neutral-950 border border-neutral-800 hover:border-zinc-700 text-slate-400 hover:text-white transition-all scale-100 cursor-pointer shrink-0"
                  title="Copy Node Generation Script"
                >
                  {copiedText ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>

              <div className="pt-1.5 flex justify-between items-center text-[8px] font-mono text-neutral-500 uppercase">
                <span>SecurePayment Account Ledger Verified</span>
                <span>Mirrored To NexoraX Resource Library</span>
              </div>
            </div>

          </div>

          {/* NexoraX billing records Info Box */}
          <div className="p-4 bg-zinc-950/90 border border-zinc-900 rounded-2xl space-y-2 text-xs">
            <div className="flex items-center gap-1.5 text-amber-500 text-[10.5px] font-bold uppercase">
              <BookOpen className="w-4 h-4 text-amber-500" />
              NexoraX Legal Directives &amp; Guidelines
            </div>
            <p className="text-zinc-500 text-[10px] uppercase tracking-wider leading-relaxed">
              All compiled print matrix vectors conform to UK statutory mandates. Placement of these QR codes on regional television routing arrays maps automatically to NexoraX billing records grant registers (NI: Secure Credential Stored Server-Side) without further administrative steps.
            </p>
          </div>

        </div>

      </div>

      {/* Centralized NexoraX Asset Hub - QR Code Library Integration */}
      <div className="bg-[#02050b] p-6 border-2 border-amber-500/40 rounded-3xl font-mono shadow-[0_0_40px_rgba(245,158,11,0.12)] relative overflow-hidden space-y-6">
        <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-amber-500/5 rounded-full blur-[100px] pointer-events-none" />
        
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 border-b border-neutral-900 pb-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-amber-500 animate-pulse" />
              <h2 className="text-sm font-black uppercase text-white tracking-[0.2em]">
                NexoraX QR Asset Hub
              </h2>
            </div>
            <p className="text-[10px] text-neutral-500 uppercase tracking-widest">
              Centralized QR management and bank-verified dispatching matrix
            </p>
          </div>
          
          <div className="flex items-center gap-2.5 bg-neutral-950 p-2 border border-zinc-900 rounded-xl px-4 text-[9px] text-[#00ffcc]">
            <Server className="w-3.5 h-3.5" />
            <span>Vault Status: CENTRAL SYNCED</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
          {/* Library Nodes Directory Feed */}
          <div className="lg:col-span-7 flex flex-col space-y-3">
            <span className="text-[10px] font-bold uppercase text-neutral-400">
              NexoraX QR Repository Directory (100 Active Targets):
            </span>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 h-72 overflow-y-auto custom-scrollbar p-1.5 bg-black rounded-2xl border border-neutral-900">
              {Array.from({ length: 100 }, (_, i) => `Node-CAT-${i + 1}`).map(node => (
                <button 
                  key={node} 
                  onClick={() => {
                    setSelectedHubNode(node);
                    setHubLogs(prev => [...prev, `[AUDIT] Inspected central repository node: ${node}`]);
                  }}
                  className={`p-2.5 border rounded-xl text-[10px] uppercase font-bold text-center transition-all cursor-pointer ${
                    selectedHubNode === node 
                      ? 'bg-amber-500/15 border-amber-500 text-amber-400 shadow-[0_0_10px_rgba(245,158,11,0.25)]' 
                      : 'border-neutral-900 bg-[#030612]/60 text-slate-400 hover:border-neutral-700 hover:text-white'
                  }`}
                >
                  {node} <span className="text-[8px] text-slate-600 block sm:inline">[QR]</span>
                </button>
              ))}
            </div>
          </div>

          {/* Active Inspected Hub Node Details Display */}
          <div className="lg:col-span-5 flex flex-col justify-between p-5 bg-neutral-950/80 border border-neutral-850 rounded-2xl space-y-4">
            <div className="space-y-3 text-left">
              <span className="text-[10px] font-extrabold tracking-widest text-[#00f0ff] uppercase bg-cyan-500/10 px-2.5 py-0.5 rounded">
                Inspected Node Details
              </span>
              <h3 className="text-base font-extrabold text-white tracking-wide uppercase">
                {selectedHubNode}
              </h3>
              
              <div className="space-y-2 border-t border-neutral-900 pt-3 text-[10px] text-slate-400">
                <div className="flex justify-between items-center py-1">
                  <span>Routing Target:</span>
                  <span className="text-white font-bold select-all">SecurePayment Checkout Portal</span>
                </div>
                <div className="flex justify-between items-center py-1">
                  <span>3% Community Allocation Ratio:</span>
                  <span className="text-amber-500 font-bold">1% Perpetual Levy</span>
                </div>
                <div className="flex justify-between items-center py-1 flex-wrap gap-1 leading-relaxed">
                  <span>Merchant Sort Code:</span>
                  <span className="text-emerald-400 font-bold">Verified Payment Configuration (SecurePayment)</span>
                </div>
                <div className="flex justify-between items-center py-1 flex-wrap gap-1 leading-relaxed">
                  <span>Merchant Account:</span>
                  <span className="text-emerald-400 font-bold">Secure Billing Provider</span>
                </div>
                <div className="flex justify-between items-center py-1">
                  <span>Archival Status:</span>
                  <span className="text-[#00ffcc] font-bold">Sync link secured</span>
                </div>
              </div>
            </div>

            {/* Simulated Live Dispatch Telemetry Readout */}
            {hubLogs.length > 0 && (
              <div className="bg-black border border-neutral-900/60 p-3 rounded-xl h-24 overflow-y-auto custom-scrollbar space-y-1 font-mono text-[9px]">
                {hubLogs.map((log, index) => (
                  <div key={index} className={log.startsWith('[SUCCESS]') || log.includes('Verified Payment Configuration') ? 'text-emerald-400' : 'text-neutral-400'}>
                    &gt; {log}
                  </div>
                ))}
              </div>
            )}

            {/* Swarm Progress bar if dispatching */}
            {hubDispatchStatus === 'DISPATCHING' && (
              <div className="space-y-1.5 font-mono">
                <div className="flex justify-between text-[9px] text-[#00ffcc]">
                  <span>Dispatch Progress:</span>
                  <span>{hubDispatchProgress}%</span>
                </div>
                <div className="w-full h-2 bg-slate-950 border border-neutral-900 rounded-full p-[1px] overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-amber-500 to-[#00ffcc] rounded-full" style={{ width: `${hubDispatchProgress}%` }} />
                </div>
              </div>
            )}

            {/* Bulk Dispatch Trigger */}
            <button 
              disabled={hubDispatchStatus === 'DISPATCHING'}
              onClick={() => {
                if (hubDispatchStatus === 'DISPATCHING') return;
                setHubDispatchStatus('DISPATCHING');
                setHubDispatchProgress(0);
                setHubLogs([
                  `[SWARM ENGINE] Commencing Grand Unified Swarm dispatch...`,
                  `[AUTHENTICATION] Bank-verified authorization token confirmed: ${hubVerifiedToken}`,
                  `[PLATFORMS] Broadcasting saturation stream: LINKEDIN, X, TIKTOK, INSTAGRAM, FACEBOOK, YOUTUBE`
                ]);

                let progressVal = 0;
                const interval = setInterval(() => {
                  progressVal += 20;
                  if (progressVal > 100) progressVal = 100;
                  setHubDispatchProgress(progressVal);

                  if (progressVal === 20) {
                    setHubLogs(prev => [...prev, `[SLA STANDARD] Calibrating 0.8s round-trip latency loop...`, `> Target grid coordinates injected successfully.`]);
                  } else if (progressVal === 60) {
                    setHubLogs(prev => [...prev, `[LEVY] Allocating 1% 3% Community Allocation metrics...`, `> Charities 1 (Dunfermline), 2 (Global), and 3 (UK) synchronized.`]);
                  } else if (progressVal === 100) {
                    clearInterval(interval);
                    setHubDispatchStatus('DONE');
                    setHubLogs(prev => [
                      ...prev, 
                      `[SUCCESS] VIRAL SWARM DISPATCHED: 10,000 ASSETS CORROSIVELY LIVE!`,
                      `[SecurePayment] Operation net routed directly to Secure Payment Provider Sort Code Verified Payment Configuration Account Secure Billing Provider.`
                    ]);
                    setSimulatedGrossHarvest(prev => prev + 180000);
                  }
                }, 180);
              }}
              className={`w-full py-3.5 rounded-xl font-black uppercase text-xs tracking-widest font-mono transition-all duration-300 transform active:scale-95 flex items-center justify-center gap-2 ${
                hubDispatchStatus === 'DISPATCHING'
                  ? 'bg-slate-900 border border-neutral-800 text-slate-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black shadow-[0_0_20px_rgba(245,158,11,0.2)] cursor-pointer'
              }`}
            >
              <Send className="w-3.5 h-3.5 fill-current" />
              {hubDispatchStatus === 'DISPATCHING' 
                ? 'Dispatching Stream...' 
                : hubDispatchStatus === 'DONE' 
                  ? 'Dispatched! Send Swarm Again' 
                  : 'Bulk Dispatch To Social Swarm'}
            </button>
          </div>
        </div>
      </div>

      {/* NexoraX Neural Push Engine - High-Conversion Notification Relay */}
      <div className="bg-black/90 p-6 border-2 border-amber-500/50 rounded-3xl font-mono shadow-[0_0_50px_rgba(245,158,11,0.15)] relative overflow-hidden space-y-6">
        <div className="absolute top-0 right-0 w-[200px] h-[200px] bg-amber-500/5 rounded-full blur-[80px] pointer-events-none" />
        
        {/* Glowing top line */}
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-amber-400 to-transparent animate-pulse" />

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 border-b border-neutral-900 pb-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-500 animate-bounce" />
              <h2 className="text-sm font-black uppercase text-white tracking-[0.2em] flex items-center gap-2">
                Neural Push Engine
                <span className="flex h-2.5 w-2.5 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00ffcc] opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#00ffcc]"></span>
                </span>
              </h2>
            </div>
            <p className="text-[10px] text-neutral-500 uppercase tracking-widest">
              High-Conversion Notification Relay // SLA: 0.8s Active Monitored Grid
            </p>
          </div>
          
          <div className="flex items-center gap-2 bg-neutral-950 p-2 border border-zinc-900 rounded-xl px-4 text-[9px] text-[#00ffcc]">
            <Activity className="w-3.5 h-3.5 text-[#00ffcc]" />
            <span>SLA Standard: 0.8s Latency Active</span>
          </div>
        </div>

        {/* Technical specifications subheader */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 bg-neutral-950 rounded-2xl border border-neutral-900 text-center text-[10px]">
          <div>
            <span className="text-neutral-500 block uppercase">Monitoring Grid</span>
            <span className="text-white font-black">10,000 Nodes Active</span>
          </div>
          <div>
            <span className="text-neutral-500 block uppercase">Real-Time SLA</span>
            <span className="text-amber-500 font-black">0.8s Round-Trip</span>
          </div>
          <div>
            <span className="text-neutral-500 block uppercase">Target Bank Node</span>
            <span className="text-emerald-400 font-black">SecurePayment: Verified Payment Configuration</span>
          </div>
          <div>
            <span className="text-neutral-500 block uppercase">NexoraX Protocol</span>
            <span className="text-[#00ffff] font-black">1% Levy / 20% Tax</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
          {/* Controllers & Interactive Simulation */}
          <div className="lg:col-span-6 space-y-4 flex flex-col justify-between">
            <div className="space-y-3">
              <span className="text-[10px] font-bold uppercase text-neutral-400 block">
                Command Dispatch Control Console:
              </span>
              <p className="text-[10px] leading-relaxed text-zinc-400 uppercase">
                Initialize native browser pushes and floating UI overlays instantly upon user checkout simulation. All processed assets cascade direct 1% Levy shares and 10% Maintenance & Hosting Fee to accounting nodes before settling balances.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              {/* Manual Conversion Injector */}
              <button
                type="button"
                onClick={() => {
                  const randomNodeNum = Math.floor(Math.random() * 10000) + 1;
                  const randomAmount = Math.floor(Math.random() * 900) + 100; // £100 - £1000
                  triggerPushNotification(`CAT-${randomNodeNum}`, randomAmount);
                }}
                className="flex-1 py-14 px-4 bg-[#030612] border-2 border-neutral-850 hover:border-amber-500/50 rounded-2xl text-[10px] uppercase font-black tracking-widest text-[#00ffcc] hover:text-white transition-all transform hover:scale-[1.01] active:scale-98 cursor-pointer text-center flex flex-col items-center justify-center gap-3 shadow-[0_4px_20px_rgba(0,0,0,0.5)]"
              >
                <div className="p-3.5 bg-neutral-950 rounded-full border border-neutral-800 text-amber-500 group-hover:text-amber-400 transition-colors">
                  <Sparkles className="w-5 h-5 animate-pulse" />
                </div>
                <span>Inject Conversion Event</span>
                <span className="text-[8px] text-neutral-500 font-medium">Auto-calculates Levy & Net Settlements</span>
              </button>

              {/* Toggle Auto Simulation */}
              <button
                type="button"
                onClick={() => {
                  setIsPushSimulationActive(!isPushSimulationActive);
                  setPushLogs(prev => [
                    { 
                      id: Date.now().toString(), 
                      time: new Date().toLocaleTimeString(), 
                      msg: `User requested simulation state toggle: ${!isPushSimulationActive ? 'ACTIVE' : 'STANDBY'}`, 
                      type: 'INFO' 
                    },
                    ...prev
                  ]);
                }}
                className={`flex-1 py-14 px-4 border-2 rounded-2xl text-[10px] uppercase font-black tracking-widest transition-all transform hover:scale-[1.01] active:scale-98 cursor-pointer text-center flex flex-col items-center justify-center gap-3 shadow-[0_4px_20px_rgba(0,0,0,0.5)] ${
                  isPushSimulationActive
                    ? 'bg-rose-950/20 border-rose-500/50 text-rose-400 hover:text-rose-300'
                    : 'bg-emerald-950/20 border-emerald-500/50 text-emerald-400 hover:text-emerald-300'
                }`}
              >
                <div className={`p-3.5 bg-neutral-950 rounded-full border border-neutral-800 ${isPushSimulationActive ? 'text-rose-500' : 'text-emerald-500'}`}>
                  {isPushSimulationActive ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                </div>
                <span>{isPushSimulationActive ? "Halt Saturation Loop" : "Enable 0.8s Telemetry Feed"}</span>
                <span className="text-[8px] text-neutral-500 font-medium">
                  {isPushSimulationActive ? "Currently Pulsing Nodes" : "Simulate live customer checkouts"}
                </span>
              </button>
            </div>
          </div>

          {/* Live scrolling Conversion Relay Telemetry Ledger */}
          <div className="lg:col-span-6 flex flex-col space-y-3">
            <span className="text-[10px] font-bold uppercase text-neutral-400 block">
              High-Conversion Notification Stream:
            </span>
            <div className="flex-1 bg-black rounded-2xl border border-neutral-900 p-4 h-52 overflow-y-auto custom-scrollbar font-sans text-[10px] space-y-2 text-left">
              {pushLogs.map((log) => (
                <div 
                  key={log.id} 
                  className={`p-2 rounded-xl flex items-start gap-2.5 transition-all text-[10px] font-mono border ${
                    log.type === 'ALERT'
                      ? 'bg-amber-950/15 border-amber-500/20 text-white'
                      : log.type === 'SYNC'
                        ? 'bg-cyan-950/15 border-cyan-500/20 text-cyan-400'
                        : 'bg-neutral-950/80 border-neutral-900 text-neutral-400'
                  }`}
                >
                  <span className="text-neutral-600 font-bold text-[9px] select-none uppercase pt-0.5">{log.time}</span>
                  <div className="flex-1 space-y-1">
                    <p className="font-semibold leading-normal">{log.msg}</p>
                    {log.type === 'ALERT' && (
                      <div className="grid grid-cols-3 gap-2.5 pt-1 mt-1 border-t border-amber-500/10 text-[8.5px] font-mono text-neutral-500 uppercase">
                        <div>
                          <span>1% Levy:</span>
                          <span className="text-amber-500 block font-bold">Allocated</span>
                        </div>
                        <div>
                          <span>10% Maintenance & Hosting Fee:</span>
                          <span className="text-cyan-400 block font-bold">Ledgered</span>
                        </div>
                        <div>
                          <span>Settlement:</span>
                          <span className="text-emerald-400 block font-bold">SecurePayment: Verified Payment Configuration</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {generatedNodes.length > 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            className="p-6 bg-[#040815]/95 rounded-3xl border border-neutral-850 font-mono space-y-4"
          >
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 border-b border-neutral-900 pb-3">
              <div className="flex items-center gap-2">
                <Layers className="w-5 h-5 text-amber-500" />
                <h3 className="text-sm font-extrabold uppercase tracking-widest text-white">
                  Active Dispatch Terminal Registry ({generatedNodes.length} Generated)
                </h3>
              </div>
              <div className="flex bg-neutral-950 border border-zinc-850 rounded-lg p-0.5 shrink-0 self-start sm:self-auto">
                <button 
                  type="button"
                  onClick={() => alert("NEXORAX ARCHIVE: Dispatching compiled high-res ZIP package containing 10,000 Vector targets.")}
                  className="py-1 px-3 text-[10px] rounded hover:bg-slate-900 text-amber-400 hover:text-amber-300 font-bold transition-all flex items-center gap-1 bg-transparent cursor-pointer"
                >
                  <Download className="w-3.5 h-3.5" />
                  ZIP Export
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs text-neutral-400">
                <thead className="text-[10px] text-neutral-500 uppercase tracking-widest border-b border-neutral-900">
                  <tr>
                    <th className="py-2.5 font-bold">Node ID Code</th>
                    <th className="py-2.5 font-bold">Target Merchant Host</th>
                    <th className="py-2.5 font-bold">Associated Branding</th>
                    <th className="py-2.5 font-bold text-center">Security Status</th>
                    <th className="py-2.5 font-bold text-right">Verification Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-900 font-mono text-[10px]">
                  {currentNodes.map((node) => (
                    <tr key={node.nodeId} className="hover:bg-slate-900/40 transition-colors">
                      <td className="py-2 text-white font-extrabold">{node.nodeId}</td>
                      <td className="py-2 text-zinc-500 truncate max-w-sm" title={node.SecurePaymentLink}>
                        {node.SecurePaymentLink}
                      </td>
                      <td className="py-2 text-cyan-400">{node.branding}</td>
                      <td className="py-2 text-center">
                        <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-wider ${
                          node.status === 'DISPATCHED' 
                            ? 'bg-emerald-950/40 border border-emerald-500/20 text-emerald-400' 
                            : 'bg-amber-950/40 border border-amber-500/20 text-amber-400'
                        }`}>
                          {node.status}
                        </span>
                      </td>
                      <td className="py-2 text-right text-zinc-500">{node.timestamp}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination controls */}
            {totalPages > 1 && (
              <div className="flex justify-between items-center text-[10px] text-neutral-500 border-t border-neutral-900 pt-3">
                <span>Showing Page {currentPage} of {totalPages}</span>
                <div className="flex gap-2">
                  <button 
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    className="py-1 px-3 border border-zinc-850 hover:border-zinc-700 bg-neutral-950 text-neutral-400 hover:text-white rounded disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer font-bold text-[9px] uppercase"
                  >
                    Previous
                  </button>
                  <button 
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    className="py-1 px-3 border border-zinc-850 hover:border-zinc-700 bg-neutral-950 text-neutral-400 hover:text-white rounded disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer font-bold text-[9px] uppercase"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Active UI Toast List - Cascading overlay for visual proof of live push events */}
      <div className="fixed bottom-6 right-6 z-[250] max-w-sm w-full space-y-3 pointer-events-none">
        <AnimatePresence>
          {activeUiToasts.map((toast) => (
            <motion.div
              key={toast.id}
              initial={{ opacity: 0, y: 30, scale: 0.9, filter: "blur(4px)" }}
              animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
              exit={{ opacity: 0, x: 80, scale: 0.9, transition: { duration: 0.2 } }}
              className="bg-[#02050b]/95 border-2 border-amber-500 rounded-2xl p-5 shadow-[0_10px_40px_rgba(245,158,11,0.25)] flex items-start gap-4 pointer-events-auto text-left relative overflow-hidden"
            >
              <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-amber-400 to-amber-600" />
              <div className="absolute top-0 right-0 w-16 h-16 bg-amber-500/5 rounded-full blur-xl pointer-events-none" />
              
              <div className="p-2 bg-amber-500/10 rounded-xl text-amber-500 border border-amber-500/20 shrink-0">
                <Bell className="w-5 h-5 animate-bounce" />
              </div>
              
              <div className="flex-1 space-y-1 font-mono">
                <div className="flex justify-between items-center">
                  <span className="text-[9px] font-black uppercase text-amber-500 tracking-widest bg-amber-500/10 px-2 py-0.5 rounded">
                    CONVERSION EVENT
                  </span>
                  <span className="text-[8px] text-neutral-500">{toast.time}</span>
                </div>
                <h4 className="text-xs font-black text-white select-all">
                  Node {toast.nodeID} Settled Live!
                </h4>
                <p className="text-sm font-black text-emerald-400 select-all">
                  +£{toast.amount.toLocaleString()}
                </p>
                <div className="pt-2 border-t border-neutral-900 mt-2 text-[8px] text-neutral-400 flex justify-between">
                  <span>Gross Reconciled</span>
                  <span className="text-neutral-500">SecurePayment Sort: Verified Payment Configuration</span>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Footer Diagnostic Channel */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-neutral-850 pt-6 text-[10px] font-mono text-zinc-500">
        <div className="flex items-center gap-1.5 uppercase">
          <Activity className="w-3.5 h-3.5 text-amber-500" />
          <span>Frequencies: 08000 622827 active</span>
        </div>
        <div className="flex items-center gap-1.5 uppercase md:justify-center">
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
          <span>Secure Payment Provider API v2.0 secured</span>
        </div>
        <div className="flex items-center gap-1.5 uppercase md:justify-end">
          <Send className="w-3.5 h-3.5 text-[#00ffcc]" />
          <span>CloudSync-02 Active Sync Vault</span>
        </div>
      </div>

    </div>
  );
};
